/*
 * Printing a night that arrives as `YYYY-MM-DD`.
 *
 * Every night here was cut at 05:00 London by the server before it was sent — `iso` on a
 * row, `first`/`last` on a week, `night` on the story — so nothing below decides which day
 * a gig belongs to. It only spells one out, and it does so through UTC so the machine's own
 * zone cannot move a date it was handed as a string. Rows themselves are printed from the
 * server's `dowShort`, `dayNum` and `monShort`; these are for the nights that have no row.
 */

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function parts(iso: string): { weekday: string; day: number; month: string } {
  const at = new Date(`${iso}T12:00:00Z`)
  return {
    weekday: DAYS[at.getUTCDay()] ?? '',
    day: at.getUTCDate(),
    month: MONTHS[at.getUTCMonth()] ?? '',
  }
}

/** `Saturday 26 September`. */
export function longNight(iso: string): string {
  const { weekday, day, month } = parts(iso)
  return `${weekday} ${day} ${month}`
}

/**
 * `Sat 26` — the day column's own label, for a night that has no row to take it from.
 *
 * The same two cells a drawn night reads off its first pick (`dowShort` `dayNum`), spelled
 * from the ISO string instead, because a night nobody picked on sends no row to read them
 * from. Here rather than in the card for the reason the file's own note gives: `parts()`
 * goes through UTC, where a `Date` built from the bare date in the card would be read back
 * in the machine's zone and name the day before anywhere west of it.
 */
export function shortDay(iso: string): string {
  const { weekday, day } = parts(iso)
  return `${weekday.slice(0, 3)} ${day}`
}

/** `Sat 26 Sep`. */
export function shortNight(iso: string): string {
  const { weekday, day, month } = parts(iso)
  return `${weekday.slice(0, 3)} ${day} ${month.slice(0, 3)}`
}

/** `Sep` — for a night that stands for a month, as the ends of the announced line do. */
export function shortMonth(iso: string): string {
  return parts(iso).month.slice(0, 3)
}

/** `21 – 27 September`, or `28 September – 4 October` when the week crosses a month. */
export function span(first: string, last: string): string {
  const from = parts(first)
  const to = parts(last)
  return from.month === to.month
    ? `${from.day} – ${to.day} ${to.month}`
    : `${from.day} ${from.month} – ${to.day} ${to.month}`
}
